/**
 * Categories Service
 *
 * Data access layer for categories table.
 * Handles all category-related database operations.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import { mergeCategoryTranslation } from '../lib/translations';
import type { AccessControl } from '../lib/accessControl';
import type { Database } from '../lib/database.types';

type CategoryInsert = Database['public']['Tables']['categories']['Insert'];
type CategoryUpdate = Database['public']['Tables']['categories']['Update'];

/**
 * Fetch all categories
 *
 * @param accessControl - User's access control info for merchant filtering
 * @param filters - Optional filters for categories
 * @param language - Language code for translations (default: 'en')
 * @param defaultLanguage - Merchant's default language. When equal to `language`
 *   or unset, translation merge is skipped and the main-table values are
 *   returned as-is. This prevents stale `category_translations` rows from
 *   overriding freshly-updated main-table values.
 */
export async function getCategories(
  accessControl: AccessControl | null,
  filters?: {
    active?: boolean;
    searchQuery?: string;
  },
  language: string = 'en',
  defaultLanguage?: string
) {
  try {
    let query = supabase
      .from('categories')
      .select('*')
      .order('display_order', { ascending: true })
      .order('category_name', { ascending: true });

    // Apply merchant filtering (RLS also enforces this, but explicit filtering is clearer)
    if (!accessControl?.isPlatformAdmin && accessControl?.merchantId) {
      query = query.eq('merchant_id', accessControl.merchantId);
    }

    if (filters?.active !== undefined) {
      query = query.eq('active', filters.active);
    }

    if (filters?.searchQuery) {
      query = query.or(
        `category_name.ilike.%${filters.searchQuery}%,category_slug.ilike.%${filters.searchQuery}%`
      );
    }

    const { data, error } = await query;
    if (error) throw error;

    // Skip translation merge for the default language — default-language
    // values live in the main table. Also skip when defaultLanguage is unset
    // so callers that forget to pass it don't pull in stale translation rows.
    if (!defaultLanguage || language === defaultLanguage) {
      return { data: data || [], error: null };
    }

    // Fetch translations for all categories
    const categoriesWithTranslations = await Promise.all(
      (data || []).map(async (category) => {
        const { data: translation } = await supabase
          .from('category_translations')
          .select('*')
          .eq('category_id', category.id)
          .eq('language_code', language)
          .maybeSingle();

        return mergeCategoryTranslation(category, translation);
      })
    );

    return { data: categoriesWithTranslations, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Fetch a single category by ID
 *
 * @param id - Category ID
 * @param accessControl - User's access control info for merchant filtering
 * @param language - Language code for translations (default: 'en')
 * @param defaultLanguage - Merchant's default language. When equal to
 *   `language` or unset, translation merge is skipped and main-table values
 *   are returned as-is.
 */
export async function getCategoryById(
  id: string,
  accessControl: AccessControl | null,
  language: string = 'en',
  defaultLanguage?: string
) {
  try {
    let query = supabase
      .from('categories')
      .select('*')
      .eq('id', id);

    // Apply merchant filtering (RLS also enforces this)
    if (!accessControl?.isPlatformAdmin && accessControl?.merchantId) {
      query = query.eq('merchant_id', accessControl.merchantId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) throw error;
    if (!data) return { data: null, error: null };

    // Skip translation merge for the default language —
    // default language values are stored in the main table.
    if (!defaultLanguage || language === defaultLanguage) {
      return { data, error: null };
    }

    // Fetch translation
    const { data: translation } = await supabase
      .from('category_translations')
      .select('*')
      .eq('category_id', data.id)
      .eq('language_code', language)
      .maybeSingle();

    const categoryWithTranslation = mergeCategoryTranslation(data, translation);

    return { data: categoryWithTranslation, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new category
 */
export async function createCategory(category: CategoryInsert) {
  try {
    const { data, error } = await supabase
      .from('categories')
      .insert(category)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update an existing category
 */
export async function updateCategory(id: string, updates: CategoryUpdate) {
  try {
    const { data, error } = await supabase
      .from('categories')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Soft delete a category by setting active to false
 */
export async function deactivateCategory(id: string) {
  try {
    const { data, error } = await supabase
      .from('categories')
      .update({
        active: false,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Check if category has products
 */
export async function checkCategoryHasProducts(categoryId: string) {
  try {
    const { count, error } = await supabase
      .from('product_types')
      .select('*', { count: 'exact', head: true })
      .eq('category_id', categoryId);

    if (error) throw error;

    return { count: count || 0, error: null };
  } catch (error) {
    return { count: 0, error: handleSupabaseError(error) };
  }
}

/**
 * Permanently delete a category (only if no products are using it)
 */
export async function deleteCategory(id: string) {
  try {
    // Check if any products are using this category
    const { count, error: countError } = await checkCategoryHasProducts(id);

    if (countError) {
      return { error: countError };
    }

    if (count && count > 0) {
      return {
        error: `Cannot delete category: ${count} product${count > 1 ? 's are' : ' is'} still using this category. Please reassign or delete those products first.`
      };
    }

    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

/**
 * Get categories count by status
 *
 * @param accessControl - User's access control info for merchant filtering
 */
export async function getCategoriesCount(accessControl: AccessControl | null) {
  try {
    // Use getCategories with no filters to get the filtered list
    const { data: categories, error } = await getCategories(accessControl);

    if (error) throw error;

    const stats = {
      total: categories?.length || 0,
      active: categories?.filter((c) => c.active === true).length || 0,
      inactive: categories?.filter((c) => c.active === false).length || 0,
    };

    return { data: stats, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Upsert category translation
 * Creates or updates a translation for a specific language
 */
export async function upsertCategoryTranslation(
  categoryId: string,
  languageCode: string,
  translation: {
    category_name: string;
    description?: string | null;
  }
) {
  try {
    const { data, error } = await supabase
      .from('category_translations')
      .upsert(
        {
          category_id: categoryId,
          language_code: languageCode,
          ...translation,
        },
        {
          onConflict: 'category_id,language_code',
        }
      )
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete a category translation row for a given language.
 *
 * Used to keep the invariant that default-language content lives only in the
 * main `categories` table — any stale row in `category_translations` for the
 * default language should be cleaned up on save so it can't override the
 * freshly-updated main-table value on later reads.
 */
export async function deleteCategoryTranslation(categoryId: string, languageCode: string) {
  try {
    const { error } = await supabase
      .from('category_translations')
      .delete()
      .eq('category_id', categoryId)
      .eq('language_code', languageCode);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

/**
 * Get all translations for a category
 */
export async function getCategoryTranslations(categoryId: string) {
  try {
    const { data, error } = await supabase
      .from('category_translations')
      .select('*')
      .eq('category_id', categoryId);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}
